import type { RangeParams, SeriesPoint } from "./api";

export const nf = new Intl.NumberFormat("id-ID");

const HOUR = 3600000;
const DAY = 86400000;

export type RangeKind = "today" | "24h" | "7d" | "30d";

export const RANGE_OPTIONS: Array<{ kind: RangeKind; label: string }> = [
  { kind: "today", label: "Hari ini" },
  { kind: "24h", label: "24 Jam" },
  { kind: "7d", label: "7 Hari" },
  { kind: "30d", label: "30 Hari" },
];

export function buildRange(kind: RangeKind, now: number, tzOffsetMinutes: number): RangeParams {
  const offset = tzOffsetMinutes * 60000;
  const shifted = now + offset;
  const dayStart = Math.floor(shifted / DAY) * DAY - offset;

  switch (kind) {
    case "today":
      return { from: dayStart, to: dayStart + DAY, unit: "hour" };
    case "24h": {
      const hourStart = Math.floor(shifted / HOUR) * HOUR - offset;
      return { from: hourStart - 23 * HOUR, to: hourStart + HOUR, unit: "hour" };
    }
    case "7d":
      return { from: dayStart - 6 * DAY, to: dayStart + DAY, unit: "day" };
    case "30d":
      return { from: dayStart - 29 * DAY, to: dayStart + DAY, unit: "day" };
  }
}

export function buildSeries(range: RangeParams, rows: SeriesPoint[]): SeriesPoint[] {
  const step = range.unit === "hour" ? HOUR : DAY;
  const map = new Map(rows.map((row) => [row.ts, row]));
  const out: SeriesPoint[] = [];
  for (let ts = range.from; ts < range.to; ts += step) {
    out.push(map.get(ts) ?? { ts, pageviews: 0, visitors: 0, visits: 0, bounces: 0, total_time: 0 });
  }
  return out;
}

export function bucketLabel(ts: number, unit: "hour" | "day", tz: string | undefined): string {
  const date = new Date(ts);
  try {
    if (unit === "hour") {
      return new Intl.DateTimeFormat("id-ID", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
    }
    return new Intl.DateTimeFormat("id-ID", { timeZone: tz, day: "numeric", month: "short" }).format(date);
  } catch {
    return date.toISOString().slice(unit === "hour" ? 11 : 5, unit === "hour" ? 16 : 10);
  }
}

export function minuteLabel(ts: number, tz: string | undefined): string {
  const date = new Date(ts);
  try {
    return new Intl.DateTimeFormat("id-ID", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
  } catch {
    return date.toISOString().slice(11, 16);
  }
}

export function formatDuration(ms: number): string {
  if (!ms || ms <= 0) return "0 d";
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds} d`;
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) return `${minutes} m ${totalSeconds % 60} d`;
  const hours = Math.floor(minutes / 60);
  return `${hours} j ${minutes % 60} m`;
}

export function formatPercent(part: number, total: number): string {
  if (!total) return "0%";
  return `${((part / total) * 100).toFixed(1).replace(".", ",")}%`;
}

export function tzOffsetMinutes(tz: string, date = new Date()): number {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(date);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
    const asUTC = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"));
    return Math.round((asUTC - date.getTime()) / 60000);
  } catch {
    return 0;
  }
}

export const TZ_CHOICES: string[] = [
  "Asia/Jakarta",
  "Asia/Makassar",
  "Asia/Jayapura",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Asia/Kolkata",
  "Asia/Dubai",
  "UTC",
  "Europe/London",
  "Europe/Berlin",
  "America/New_York",
  "America/Los_Angeles",
  "Australia/Sydney",
];
